import { localizedStrings } from '@core/strings'
import React, { useMemo } from 'react'
import { Text, Box, Flex } from 'webapp/components/common/fundamentals'
import { Card, Divider, IconButton } from '@common/material'
import { CardContent, Typography } from '@mui/material'
import moment from 'moment'
import { Checklist } from '@core/entities/checklist/Checklist'
import { useModal } from '@common/GlobalModal'
import { PageUrls } from '@core/page-urls'
import { Section } from '@common/Section'
import { RegisterItemFlatPropertyData } from '@core/entities/register/RegisterItem'
import { Property } from '@elements/Property/Property'
import { RegisterItemPropertyValue } from '@modules/registers/propertyValue/RegisterItemPropertyValue'
import { Collapsible } from '@common/Collapsible'
import { OpenInNewOutlined } from '@mui/icons-material'
import ChevronRight from '@mui/icons-material/ChevronRight'
import { SimpleMapModal } from '@modules/geolocation'
import useConstants from '@core/providers/useConstants'
import { useRouter } from 'next/router'
import { BriefingSessionCard } from '@modules/briefings'
import { UserProfileHoverWrapper } from '@elements/UserProfileHoverWrapper'
import { InspectionQuestionsStats } from '@modules/template-form/InspectionQuestionsStats'
import { ReviewChecklistCard } from './ReviewChecklistCard'
import { RequestedChangesCard } from './RequestedChangesCard'

type ChecklistDetailsSidebarProps = {
  checklist: Checklist
  organisationId: number
  projectId?: number
}

export const ChecklistDetailsSidebar: React.FC<
  ChecklistDetailsSidebarProps
> = ({ checklist, projectId, organisationId }) => {
  const { showModal } = useModal()
  const { constants } = useConstants()
  const router = useRouter()

  const registerItem = checklist.registerInspection?.registerItem
  const registerId = checklist.registerInspection?.registerId
  const usedForInspections = checklist.template.usedForInspections

  const memoisedUniqueTableColumns: RegisterItemFlatPropertyData[] =
    useMemo(() => {
      const nameCounts = new Map<string, number>()

      const uniqueColumnNames = registerItem?.data?.map((column) => {
        const currentCount = nameCounts.get(column.name) || 0
        const columnName = `${column.name}${
          currentCount ? ` (${currentCount})` : ''
        }`

        // Update the count in the map for the next occurrence
        nameCounts.set(column.name, currentCount + 1)

        const columnValue =
          registerItem.getRegisterItemPropertyDisplayValues(column)

        return { ...column, name: columnName, value: columnValue }
      })

      return uniqueColumnNames || []
    }, [])

  return (
    <Box>
      <ReviewChecklistCard
        organisationId={organisationId}
        projectId={projectId}
        checklist={checklist}
      />

      <RequestedChangesCard
        organisationId={organisationId}
        projectId={projectId}
        checklist={checklist}
      />

      {checklist.shouldShowBriefingCard() && (
        <BriefingSessionCard
          sx={{ mb: 2 }}
          entityId={checklist.id}
          entityType='CHECKLIST'
          briefingSession={checklist.lastBriefingSession}
        />
      )}

      <Card>
        <CardContent>
          <Typography
            sx={{ fontSize: '16px' }}
            variant='h6'
            color='text.primary'
            gutterBottom
          >
            {localizedStrings.generalInformation}
          </Typography>
          <Divider />

          <Section label={localizedStrings.summary} mt={2}>
            <Text data-test='permit-summary' showTooltip>
              {checklist.summary}
            </Text>
          </Section>

          <Section label={localizedStrings.project} mt={2}>
            <Text
              showTooltip
              color='primary.main'
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                router.push(
                  PageUrls.project(
                    checklist.organisationId,
                    checklist.project.id,
                  ),
                )
              }}
            >
              {checklist.project.name}
            </Text>
          </Section>

          <Section label={localizedStrings.location} mt={2}>
            <Text showTooltip>{checklist.locationName}</Text>
          </Section>

          <Section label={localizedStrings.uniqueCode} mt={2}>
            <Text showTooltip>{checklist.shortUUID}</Text>
          </Section>

          {checklist.getCheckinLocation() && (
            <Section label={localizedStrings.geoLocation} mt={2}>
              <Text
                color='primary.main'
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  showModal(
                    <SimpleMapModal
                      projectId={checklist.project.id}
                      featurePoint={checklist.getCheckinLocation()}
                    />,
                  )
                }}
              >
                {localizedStrings.showOnMap}
              </Text>
            </Section>
          )}

          {/* TODO(SQA-75): Drop once backend stops populating ownedByRegisterItem.
              Kept read-only so legacy checklists still surface their linked register item. */}
          {checklist?.ownedByRegisterItem && (
            <Section label={localizedStrings.registerItem} mt={2}>
              <Text
                onClick={() => {
                  window.open(
                    PageUrls.projectRegisterItemDetails({
                      organisationId: checklist.organisationId,
                      projectId: checklist.project.id,
                      registerId: checklist.ownedByRegisterItem.register.id,
                      registerItemId:
                        checklist.ownedByRegisterItem.registerItem.id,
                    }),
                    '_blank',
                    'noopener, noreferrer',
                  )
                }}
                color='primary.main'
                sx={{ cursor: 'pointer' }}
                showTooltip
              >{`${checklist.ownedByRegisterItem.register.name} - ${checklist.ownedByRegisterItem.registerItem.uniqueId}`}</Text>
            </Section>
          )}

          <Section label={localizedStrings.date} mt={2}>
            <Text showTooltip>
              {moment(checklist.createdOn).format('Do MMM YYYY HH:mm')}
            </Text>
          </Section>
        </CardContent>
      </Card>

      {usedForInspections && !!checklist.stats && (
        <InspectionQuestionsStats
          stats={checklist.stats}
          variant='checklistDetails'
        />
      )}

      <Card sx={{ marginTop: 2 }}>
        <CardContent>
          <Text
            showTooltip
            sx={{ fontSize: '16px' }}
            variant='h6'
            color='text.primary'
            gutterBottom
          >
            {localizedStrings.people}
          </Text>
          <Divider />

          {checklist?.createdBy && (
            <Section label={localizedStrings.author} mt={2}>
              <Flex
                justifyContent='space-between'
                alignItems='center'
                onClick={() =>
                  router.push(
                    PageUrls.organisationUserProfile({
                      organisationId,
                      userId: checklist.createdBy.id,
                    }),
                  )
                }
                sx={{ cursor: 'pointer' }}
              >
                <UserProfileHoverWrapper
                  userId={checklist.createdBy.id}
                  organisationId={organisationId}
                >
                  <Text>{checklist.createdBy.getFullName()}</Text>
                </UserProfileHoverWrapper>
                <ChevronRight />
              </Flex>
            </Section>
          )}

          {!!checklist?.participants?.length && (
            <>
              <Text
                mt={4}
                sx={{ fontSize: '16px' }}
                variant='h6'
                color='text.primary'
                gutterBottom
                showTooltip
              >
                {localizedStrings.participants}
              </Text>
              <Divider sx={{ mb: 1 }} />

              {checklist?.participants.map((participant) => {
                return (
                  <Section
                    key={participant.id}
                    label={participant.getRole()}
                    mt={2}
                  >
                    <Flex
                      justifyContent='space-between'
                      alignItems='center'
                      onClick={() =>
                        participant.isSiteAssistUser() &&
                        router.push(
                          PageUrls.organisationUserProfile({
                            organisationId,
                            userId: participant.userId,
                          }),
                        )
                      }
                      sx={{ cursor: 'pointer' }}
                    >
                      <UserProfileHoverWrapper
                        userId={participant.userId}
                        externalUser={participant}
                        organisationId={organisationId}
                      >
                        <Text>{participant.getFullName()}</Text>
                      </UserProfileHoverWrapper>
                      <ChevronRight />
                    </Flex>
                  </Section>
                )
              })}
            </>
          )}
        </CardContent>
      </Card>

      {!!registerItem && (
        <Card sx={{ marginTop: 2 }} data-test='register-item-section'>
          <CardContent>
            <Flex justifyContent='space-between' alignItems='center'>
              <Text
                showTooltip
                sx={{ fontSize: '16px' }}
                variant='h6'
                color='text.primary'
                gutterBottom
              >
                {localizedStrings.registerItem}
              </Text>

              <IconButton
                color='primary'
                sx={{ height: 'fit-content', alignSelf: 'center' }}
                onClick={() => {
                  const url = PageUrls.projectRegisterItemDetails({
                    organisationId: checklist.organisationId,
                    projectId: checklist.project.id,
                    registerId,
                    registerItemId: registerItem.id,
                  })

                  window.open(url, '_blank', 'noopener, noreferrer')
                }}
              >
                <OpenInNewOutlined />
              </IconButton>
            </Flex>
            <Divider sx={{ mb: 2 }} />

            {memoisedUniqueTableColumns
              ?.slice(0, constants.max_register_card_properties)
              ?.map(
                (item: RegisterItemFlatPropertyData & { value: string }) => {
                  if (item.type === 'INSPECTION') return null

                  return (
                    <Property
                      key={item.registerPropertyId}
                      heading={item.name}
                      subHeadingComponent={
                        <RegisterItemPropertyValue
                          variant='details'
                          registerItemPropertyData={item}
                        />
                      }
                      horizontal
                      sx={{
                        display: 'flex',
                        '& > p': { width: `150px` },
                      }}
                    />
                  )
                },
              )}
            {!!memoisedUniqueTableColumns?.slice(
              constants.max_register_card_properties,
            )?.length && (
              <Collapsible
                label={{
                  collapsed: localizedStrings.seeMore,
                  open: localizedStrings.hideAll,
                }}
                collapsed
              >
                {memoisedUniqueTableColumns
                  ?.slice(constants.max_register_card_properties)
                  ?.map(
                    (
                      item: RegisterItemFlatPropertyData & { value: string },
                    ) => {
                      if (item.type === 'INSPECTION') return null

                      return (
                        <Property
                          key={item.registerPropertyId}
                          heading={item.name}
                          subHeadingComponent={
                            <RegisterItemPropertyValue
                              variant='details'
                              registerItemPropertyData={item}
                            />
                          }
                          horizontal
                          sx={{
                            display: 'flex',
                            '& > p': { width: `150px` },
                          }}
                        />
                      )
                    },
                  )}
              </Collapsible>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  )
}
